import type { ReactNode } from 'react'

import _ from 'lodash'

import { useAuthContext } from '@/features/auth/model/auth-context'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { CheckboxField, Field, PasswordField, TextField } from '@/shared/ui/Form'

type SignUpFieldsProps = {
  /** URL dos termos. Quando presente, o aceite passa a ser exigido. */
  withTerms?: string | boolean
  children?: ReactNode
}

/**
 * Campos de cadastro.
 *
 * Os dois campos de senha tinham, cada um, seu próprio `InputGroup` com botão de
 * olho e rótulo fixo "Mostrar senha" — mesmo quando a senha já estava visível, e
 * mesmo no campo de confirmação, onde o rótulo dizia respeito ao outro campo.
 * Ambos passam a ser `PasswordField`.
 *
 * O aceite dos termos era `FormCheck` mais um `ErrorMessage` solto; agora é
 * `CheckboxField`, que já associa a mensagem ao controle.
 */
export function SignUpFields({ withTerms, children }: SignUpFieldsProps) {
  const { config } = useAuthContext()
  const { t } = useTranslation()

  const withEmailConfirmation = _.get(
    config,
    'signUp.withEmailConfirmation',
    false
  ) as boolean

  return (
    // Ritmo vertical pelo container, não por margem no filho (MASTER §7 L4).
    <div className="flex flex-col gap-4">
      <Field name="name">
        <Field.Label>{t('auth.name', {}, { fallback: 'Nome completo' })}</Field.Label>
        <Field.Control>
          <TextField autoComplete="name" />
        </Field.Control>
        <Field.Error />
      </Field>

      <Field name="email">
        <Field.Label>{t('auth.email', {}, { fallback: 'E-mail' })}</Field.Label>
        <Field.Control>
          <TextField autoComplete="email" inputMode="email" />
        </Field.Control>
        <Field.Error />
      </Field>

      {withEmailConfirmation && (
        <Field name="email_confirmation">
          <Field.Label>
            {t('settings.confirmationEmail', {}, { fallback: 'Confirme seu e-mail' })}
          </Field.Label>
          <Field.Control>
            {/* `autoComplete="off"`: preencher a confirmação automaticamente
                anularia o propósito de confirmar. */}
            <TextField autoComplete="off" inputMode="email" />
          </Field.Control>
          <Field.Error />
        </Field>
      )}

      <Field name="password">
        <Field.Label>{t('auth.password', {}, { fallback: 'Senha' })}</Field.Label>
        <Field.Control>
          <PasswordField autoComplete="new-password" />
        </Field.Control>
        <Field.Description>Use 8 ou mais caracteres.</Field.Description>
        <Field.Error />
      </Field>

      <Field name="password_confirmation">
        <Field.Label>
          {t('settings.confirmPassword', {}, { fallback: 'Confirme sua senha' })}
        </Field.Label>
        <Field.Control>
          <PasswordField autoComplete="new-password" />
        </Field.Control>
        <Field.Error />
      </Field>

      {children}

      {typeof withTerms === 'string' && withTerms && (
        <CheckboxField
          name="accept_terms"
          label={
            <>
              {t(
                'declareReadFullyAgreeTerms',
                {},
                { fallback: 'Declaro que li e concordo com os' }
              )}{' '}
              <a
                href={withTerms}
                target="_blank"
                rel="noreferrer"
                className="font-bold underline-offset-2 hover:underline"
                // O clique no link não pode alternar a caixa: são duas ações
                // diferentes no mesmo rótulo.
                onClick={event => event.stopPropagation()}
              >
                {t('auth.termsOfUse', {}, { fallback: 'Termos de Uso' })}
              </a>{' '}
              {t('auth.ofThePlatform', {}, { fallback: 'da plataforma' })}
            </>
          }
        />
      )}
    </div>
  )
}
